import { isolateDirection } from "@frontend-shared/lib";
import { PauseCircle } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";
import type { IAgentAutoPausedCardProps } from "./types";

// NOTE: NSAI-4259 — surfaces an agent the scheduler stopped on its own after too
// many consecutive failed runs. Previously the only signal was the row's status
// badge silently flipping to "Paused", so a dead schedule could go unnoticed
// indefinitely.
const AgentAutoPausedCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onViewRuns,
}: IAgentAutoPausedCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");

  const handleViewRuns = useCallback((): void => {
    onViewRuns(data.agentUuid, data.projectUuid);
  }, [data.agentUuid, data.projectUuid, onViewRuns]);

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="agent_auto_paused"
      createdAt={createdAt}
      isRead={isRead}
      icon={<PauseCircle className="h-5 w-5 text-destructive" />}
      iconBgClassName="bg-destructive/10"
      ctaLabel={t("view_runs")}
      onCtaClick={handleViewRuns}
      body={
        <div className="space-y-1">
          <p className="font-medium text-foreground">
            {t("type.agent_auto_paused.title")}
          </p>
          {/* NOTE: NSAI-4800 — isolated so a Latin name's trailing
              punctuation stays inside its quotes in an RTL sentence, as on
              the mobile card. */}
          <p>
            {t("type.agent_auto_paused.body", {
              agentName: isolateDirection(data.agentName),
              consecutiveErrors: data.consecutiveErrors,
            })}
          </p>
          {/* NOTE: The failing run's error is the single most useful piece of
              context for deciding whether to fix or resume, so it is rendered
              inline when the server captured one. Already truncated server-side
              (AUTO_PAUSE_ERROR_SUMMARY_MAX_LENGTH); the shell's
              `[overflow-wrap:anywhere]` keeps an unbreakable token from
              widening the drawer. Isolated because the error stays an LTR
              sentence inside an RTL line. */}
          {data.lastError && (
            <p
              className="text-xs text-muted-foreground"
              data-testid="notification-card-agent_auto_paused-last-error"
            >
              {t("type.agent_auto_paused.last_error", {
                lastError: isolateDirection(data.lastError),
              })}
            </p>
          )}
        </div>
      }
    />
  );
};

export const AgentAutoPausedCard = memo(AgentAutoPausedCardComponent);
